import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, User, Linkedin, Instagram, Twitter } from "lucide-react";
import { useMemberAuth } from "../context/MemberAuthContext";
import { Wordmark } from "./Wordmark";
import { usePageSEO } from "../hooks/usePageSEO";
import { PublicConsent } from "./CookieConsent";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/organisations", label: "Organisations" },
  { to: "/diaspora", label: "Diaspora Network" },
  { to: "/partner", label: "Partner" },
  { to: "/webinars", label: "Masterclasses" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { member, logout } = useMemberAuth();
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md"
      style={{ background: "rgba(242,232,214,0.9)", borderBottom: "1px solid rgba(18,52,40,0.08)" }}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Wordmark size={30} data-testid="site-wordmark" />
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-[14px] font-sans-body link-underline transition-colors ${
                  isActive ? "text-[color:var(--forest)] font-semibold" : "text-[color:var(--forest)]/80 hover:text-[color:var(--forest)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/donate" className="hidden md:inline-flex items-center px-4 py-2 rounded-md text-[14px] btn-ghost">
            Donate
          </Link>
          {member ? (
            <>
              <Link
                to="/member"
                className="hidden md:inline-flex items-center gap-1 px-4 py-2 rounded-md text-[14px] text-[color:var(--forest)]"
              >
                <User size={14} /> {(member.display_name || "Member").split(" ")[0]}
              </Link>
              <button onClick={logout} className="hidden md:inline-flex items-center px-4 py-2 rounded-md text-[14px] btn-forest">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline-flex items-center px-4 py-2 rounded-md text-[14px] text-[color:var(--forest)]/80 hover:text-[color:var(--forest)]">
                Sign in
              </Link>
              <Link to="/join" className="hidden md:inline-flex items-center gap-1 px-4 py-2 rounded-md text-[14px] btn-gold font-medium">
                Join the Network <ArrowUpRight size={14} />
              </Link>
            </>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-black/5 px-6 py-4 flex flex-col gap-3" style={{ background: "var(--beige)" }}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-[15px] text-[color:var(--forest)]">
              {n.label}
            </NavLink>
          ))}
          <div className="flex gap-2 pt-2 flex-wrap">
            <Link to="/donate" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-[14px] btn-ghost">
              Donate
            </Link>
            {member ? (
              <>
                <Link to="/member" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-[14px] btn-forest">
                  My account
                </Link>
                <button onClick={() => { logout(); setOpen(false); }} className="px-4 py-2 rounded-md text-[14px] btn-ghost">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-[14px] btn-ghost">
                  Sign in
                </Link>
                <Link to="/join" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-[14px] btn-gold">
                  Join the Network
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="mt-24" style={{ background: "var(--forest)", color: "var(--cream)" }} data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
      <div className="md:col-span-1">
        <Wordmark size={42} className="mb-4" />
        <p className="text-[13px] leading-relaxed max-w-[240px]" style={{ color: "rgba(255,246,226,0.75)" }}>
          Nigeria. Education. Nation-building.
        </p>
      </div>
      <div>
        <div className="section-eyebrow mb-3" style={{ color: "rgba(255,246,226,0.55)" }}>
          Navigation
        </div>
        <ul className="space-y-2 text-[14px]">
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/about">About</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/what-we-do">What We Do</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/organisations">Organisations</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/diaspora">Diaspora Network</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/partner">Partner</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/webinars">Masterclasses</Link></li>
        </ul>
      </div>
      <div>
        <div className="section-eyebrow mb-3" style={{ color: "rgba(255,246,226,0.55)" }}>Legal</div>
        <ul className="space-y-2 text-[14px]">
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/privacy">Privacy Policy</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/terms">Terms &amp; Conditions</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/donate">Donate</Link></li>
          <li><Link className="link-underline hover:text-[color:var(--gold)]" to="/apply-for-support">Apply for support</Link></li>
        </ul>
      </div>
      <div>
        <div className="section-eyebrow mb-3" style={{ color: "rgba(255,246,226,0.55)" }}>Contact</div>
        <ul className="space-y-2 text-[14px]" style={{ color: "rgba(255,246,226,0.85)" }}>
          <li>
            <a href="mailto:hello@vemoyefoundation.org" className="link-underline hover:text-[color:var(--gold)]">
              hello@vemoyefoundation.org
            </a>
          </li>
        </ul>
        <div className="mt-5 flex items-center gap-3" style={{ color: "rgba(255,246,226,0.7)" }}>
          <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="p-2 rounded-full hover:text-[color:var(--gold)]"><Linkedin size={16} /></a>
          <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 rounded-full hover:text-[color:var(--gold)]"><Instagram size={16} /></a>
          <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="X (Twitter)" className="p-2 rounded-full hover:text-[color:var(--gold)]"><Twitter size={16} /></a>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[12px]" style={{ color: "rgba(255,246,226,0.55)" }}>
        <span>© {new Date().getFullYear()} Vemoye Foundation. Registered Charity No. 1188086.</span>
        <span className="flex items-center gap-1">Made for Nigeria <ArrowUpRight size={12} /></span>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => {
  const location = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [location.pathname]);
  usePageSEO(location.pathname);
  const isAdmin = location.pathname.startsWith("/admin");
  const isMember = location.pathname.startsWith("/member") || location.pathname.startsWith("/activate") || location.pathname === "/login" || location.pathname.startsWith("/forgot-password") || location.pathname.startsWith("/reset-password");
  const chromeless = isAdmin || isMember;
  return (
    <div style={{ background: "var(--beige)" }} className="min-h-screen">
      {!chromeless && <Header />}
      <main>{children}</main>
      {!chromeless && <Footer />}
      {!chromeless && <PublicConsent />}
    </div>
  );
};

export default Layout;
